import { mapKeys, kebabCase } from 'lodash';
import sanitize from 'sanitize-filename';
import reactGA from 'react-ga';
import { toStringArray } from '~/shared/utils/array';
import {
  extractFilterHierarchyFromFilters,
  isFilterHierarchyType,
  normalizeFilterHierarchyConfig,
  omitFilterHierarchyFilters,
} from '~/shared/utils/filterHierarchy';
import { DEFAULT_MMP_TARGET_DASHBOARD_ID, isMmpPortfolioTableWidget } from '~/shared/utils/navDashboardPath';
import { api, text, download, downloadMatrix } from './core';

const stringifyParams = (params: Record<string, any>) =>
  Object.entries(params)
    .map(([key, rawValue]) => {
      const value = rawValue == null ? '' : String(rawValue);
      const encodedKey = encodeURIComponent(key);
      const encodedValue = encodeURIComponent(value).replace(/%2C/g, ',');
      return `${encodedKey}=${encodedValue}`;
    })
    .join('&');

/** Support filters.items, a bare filters array, or a single Filter Hierarchy object. */
function getRawFilterList(filters: unknown): any[] {
  if (!filters) return [];
  if (Array.isArray(filters)) return filters;
  if (typeof filters !== 'object') return [];

  const source = filters as Record<string, unknown>;
  if (Array.isArray(source.items)) return source.items;

  // Entire `filters` payload is one Filter Hierarchy (no `items` wrapper).
  if (isFilterHierarchyType(source.type) || source.hierarchy || source.filterHierarchy || source['filter-hierarchy']) {
    return [source];
  }

  return [];
}

function fixFilter(f: any) {
  if (isFilterHierarchyType(f?.type) || f?.hierarchy || f?.filterHierarchy || f?.['filter-hierarchy']) {
    return {
      ...f,
      type: 'Filter Hierarchy',
      hierarchy: f.hierarchy ?? f.filterHierarchy ?? f['filter-hierarchy'],
      options: f.options,
      defaultSelection: f.defaultSelection ?? f['default-selection'],
    };
  }

  const rawDefault = f['default-value'];
  const defaultValue =
    typeof f?.type === 'string' && f.type.toLowerCase().includes('calendar') && typeof rawDefault === 'string'
      ? rawDefault.replace(/\//g, '-')
      : rawDefault;

  const options = f.options?.map((o: any) => ({ ...o }));

  const multiSelectRaw = f['multi-select'] ?? f.multiSelect;
  const multiSelect = multiSelectRaw === true || multiSelectRaw === 'true';

  return { ...f, options, defaultValue, multiSelect };
}

function indicatorReducer(all: any, curr: any) {
  all[curr['heatmap-slice']] = curr.items.map((i: any) => {
    const targetDashboardId = i['target-dashboard-id'] ?? i.targetDashboardId;
    return targetDashboardId
      ? [i['indicator-value'], i.type || i['chart-type'], targetDashboardId]
      : [i['indicator-value'], i.type || i['chart-type']];
  });
  return all;
}

export const DEFAULT_SAR_TABLE_TARGET_DASHBOARD_ID = DEFAULT_MMP_TARGET_DASHBOARD_ID;

/**
 * Temporary FE override until API nav config fully wires SARTable clicks.
 * Default: navigate to CompanyPage with company filter; fall back to same-page filter in UI.
 */
function ensureSarTableCompanyFilterRelatedData(relatedData: any[] | undefined): any[] {
  const slices = relatedData ? relatedData.map(slice => ({ ...slice, items: [...(slice.items || [])] })) : [];
  const companyNavigateItem = {
    'indicator-value': 'company',
    'chart-type': 'navigate',
    'target-dashboard-id': DEFAULT_MMP_TARGET_DASHBOARD_ID,
  };
  const valueIdx = slices.findIndex(slice => slice['heatmap-slice'] === 'value');

  const normalizeCompanyItem = (item: any) => {
    const indicatorValue = String(item?.['indicator-value'] || '').toLowerCase();
    if (indicatorValue !== 'company') return item;
    const chartType = String(item?.['chart-type'] || item?.type || '').toLowerCase();
    const explicitTarget = item?.['target-dashboard-id'] || item?.targetDashboardId;
    // Explicit filter without a target stays same-page; otherwise default to navigate → CompanyPage.
    if (chartType === 'filter' && !explicitTarget) {
      return item;
    }
    return {
      ...item,
      'chart-type': 'navigate',
      'target-dashboard-id': explicitTarget || DEFAULT_MMP_TARGET_DASHBOARD_ID,
    };
  };

  if (valueIdx >= 0) {
    const items = slices[valueIdx].items.map(normalizeCompanyItem);
    const hasCompanyAction = items.some(
      (item: any) => String(item?.['indicator-value'] || '').toLowerCase() === 'company',
    );
    slices[valueIdx] = {
      ...slices[valueIdx],
      items: hasCompanyAction ? items : [companyNavigateItem, ...items],
    };
    return slices;
  }

  return [...slices, { 'heatmap-slice': 'value', items: [companyNavigateItem] }];
}

function navItemMapper({ id, name, menuType, filters, heatmap, dashboards, items, prisms, icon, ..._rest }: any) {
  const type = menuType;
  const mappedFilters = getRawFilterList(filters).map(fixFilter);
  // Filter Hierarchy is API-driven only (any dashboard). No name-based hard-coded trees.
  const extractedFilterHierarchy =
    extractFilterHierarchyFromFilters(mappedFilters) ??
    normalizeFilterHierarchyConfig(filters) ??
    normalizeFilterHierarchyConfig(
      _rest?.narrativeFilterTree ??
        _rest?.['narrative-filter-tree'] ??
        _rest?.filterHierarchy ??
        _rest?.['filter-hierarchy'],
    );
  const gridElements = _rest['grid-elements-lrg']?.map((gi: any, index: number) => {
    const affectedWidgetsRaw =
      gi.affectedWidgets ?? gi['affected-widgets'] ?? gi.item?.affectedWidgets ?? gi.item?.['affected-widgets'];
    const affectingParamsRaw =
      gi.affectingParams ?? gi['affecting-params'] ?? gi.item?.affectingParams ?? gi.item?.['affecting-params'];
    const affectedWidgets = toStringArray(affectedWidgetsRaw);
    const affectingParams = toStringArray(affectingParamsRaw);
    const relatedData = isMmpPortfolioTableWidget(gi)
      ? ensureSarTableCompanyFilterRelatedData(gi.item?.['related-data'])
      : gi.item?.['related-data'];
    return {
      gridDataLg: gi.gridData,
      gridDataMd: _rest['grid-elements-med'][index].gridData,
      gridDataSm: _rest['grid-elements-sml'][index].gridData,
      item: {
        type: gi.item?.type || gi.item?.['chart-type'] || 'grid',
        id: gi.id,
        name: gi.name,
        indicators: relatedData?.reduce(indicatorReducer, {}) ?? {},
        filters: omitFilterHierarchyFilters(mappedFilters),
        heatmap: { ...gi.item },
        icon: gi.icon,
        affectedWidgets,
        affectingParams,
        narrativeFilterTree: extractedFilterHierarchy,
      },
    };
  });
  const showTitle = _rest['show-title'];
  const title = _rest['title'];
  const showDownloadButton = _rest['show-download-button'] === 'true' || _rest['show-download-button'] === true;
  if (dashboards && dashboards.length > 0) {
    const subItemsdashboards = dashboards.map(navItemMapper);
    return { name, categories: subItemsdashboards, items: subItemsdashboards, type, icon };
  }
  if (items && items.length > 0) {
    const subItems = items.map(navItemMapper);
    return { name, categories: subItems, items: subItems, type, icon };
  }
  if (prisms && prisms.length > 0) {
    const subItemsPrisms = prisms.map(navItemMapper);
    return { name, categories: subItemsPrisms, items: subItemsPrisms, type, icon };
  }

  const narrativeFilterTree = extractedFilterHierarchy;

  return {
    type: heatmap?.type || heatmap?.['chart-type'] || 'grid',
    gridElements,
    id,
    showTitle,
    title,
    showDownloadButton,
    name,
    filters: omitFilterHierarchyFilters(mappedFilters),
    narrativeFilterTree,
    indicators: heatmap?.['related-data']?.reduce(indicatorReducer, {}) ?? {},
    heatmap: heatmap,
    icon,
  };
}

function calcIndicatorUrl(cellInfo: any, category: string, prism: string, indicator: string, filterValues: any) {
  const { topic: t, symbol: s } = cellInfo;
  const base = category === 'Risk' ? 'widget-data-risk' : 'widget-data';
  const preparedFilterValues = mapKeys(filterValues, (_, key) => kebabCase(key));
  const paramsWithHeatmap = {
    ...preparedFilterValues,
    indicator,
    'prism-id': prism,
    'heatmap-slice-value': t && s ? `${t}_${s}` : t || s || '',
    row: t || '',
    column: s || '',
  };
  const queries = stringifyParams(paramsWithHeatmap);
  return `${base}?${queries}`;
}

function calcHeatmapUrl(
  base: string,
  category: string,
  subcategory: string,
  subsubcategory: string,
  prism: string,
  filterValues: any,
  prismId?: string,
  dashboardId?: string,
) {
  const baseUrl = base;
  const preparedFilterValues = mapKeys(filterValues, (_, key) => kebabCase(key));
  const params = {
    ...preparedFilterValues,
    'prism-id': prismId || prism,
    'dashboard-id': dashboardId || '',
  };
  const queries = stringifyParams(params);

  return `${baseUrl}?${queries}`;
}

export const fetchNavHirarchy = async (): Promise<any> => {
  const apiDataPatch = await api('navigation-hierarchy');

  const apiData = apiDataPatch.navigationItems ? apiDataPatch : { navigationItems: apiDataPatch, searchData: [] };

  const data = [
    {
      name: '',
      type: 'heatmap',
      categories:
        apiData?.navigationItems?.map((item: any) => {
          if (item.menuType === 'Item') {
            const mappedItem = navItemMapper(item);
            return {
              ...mappedItem,
              name: item.name,
              icon: item?.icon,
              menuType: item?.menuType,
            };
          }
          return {
            name: item.name,
            dashboards: item?.dashboards?.map(navItemMapper) || [],
            items: item?.items?.map(navItemMapper) || [],
            prisms: item?.prisms?.map(navItemMapper) || [],
            icon: item?.icon,
            menuType: item?.menuType,
          };
        }) || [],
    },

    // {
    //   name: 'Build',
    //   type: 'build',
    // },
    // {
    //   name: 'Invest',
    //   type: 'invest',
    // },
  ];

  return { data, searchData: apiData?.searchItems };
};
fetchNavHirarchy.key = 'fetchNavHirarchy';

export const fetchSearchData = async (): Promise<any> => {
  let apiData: any = [];
  try {
    apiData = await api('search-data');
  } catch (error) {
    apiData = [];
  }

  return apiData;
};
fetchSearchData.key = 'fetchSearchData';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Heatmap {
  data: number[][];
  topics: string[];
  symbols: string[];
}

export const fetchHeatmap = (
  category: string,
  subcategory: string,
  subsubcategory: string,
  prism: string,
  filterValues: any,
  prismId?: string,
) => {
  return api(calcHeatmapUrl('widget-data', category, subcategory, subsubcategory, prism, filterValues, prismId));
};

export const downloadHeatmapData = (
  category: string,
  subcategory: string,
  subsubcategory: string,
  prism: string,
  filterValues: any,
  prismId?: string,
  fileBaseName?: string,
) => {
  if (category === 'Risk') {
    const values = Object.keys(filterValues)
      .sort()
      .map(key => filterValues[key]);

    const baseName = (typeof fileBaseName === 'string' && fileBaseName.trim()) || String(prism);
    const fileName = [baseName, ...values].map(value => sanitize(value)).join('-') + '.csv';

    return download(
      calcHeatmapUrl('download-matrix', category, subcategory, subsubcategory, prism, filterValues, prismId),
      fileName,
    );
  } else {
    return downloadMatrix(
      calcHeatmapUrl('download-matrix', category, subcategory, subsubcategory, prism, filterValues, prismId),
    );
  }
};

export const downloadGridData = (
  category: string,
  subcategory: string,
  subsubcategory: string,
  prism: string,
  filterValues: any,
  prismId?: string,
) => {
  return downloadMatrix(
    calcHeatmapUrl('download-dashboard', category, subcategory, subsubcategory, prism, filterValues, prismId, prismId),
  );
};

export const fetchPortfoliosLastDateAvailable = (): Promise<string> =>
  api('portfolios/last-date-available').then((date: string) => date.replace(/\//g, '-'));

export const fetchPortfolios = (date: string): Promise<string> => text(`download-portfolio?date=${date}`);

export const fetchIndicatorData = (
  category: string,
  prism: string,
  prismId: string,
  indicator: string,
  cellInfo: any,
  filterValues: any,
) => {
  const path = calcIndicatorUrl(cellInfo, category, prismId, indicator, filterValues);

  return api(path);
};

export const downloadIndicatorData = (
  category: string,
  prism: string,
  indicator: string,
  cellInfo: any,
  filterValues: any,
) => {
  const { topic: t, symbol: s } = cellInfo;
  const fileName =
    [indicator, filterValues['Reservoir'], `${t || ''}${t && s ? '_' : ''}${s || ''}`].join('-') + '.csv';

  const url = 'download-' + calcIndicatorUrl(cellInfo, category, prism, indicator, filterValues);
  reactGA.event({ category: 'User', action: 'Downloaded csv', label: url });
  return download(url, fileName);
};

export const fetchTopicExplainability = (topic: string) =>
  api(`topic-explainability?topic=${encodeURIComponent(topic)}`);
fetchTopicExplainability.key = 'fetchTopicExplainability';
